import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-heading font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-brand text-white hover:shadow-brand-hover hover:-translate-y-0.5 shadow-brand font-semibold relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-200%] hover:before:translate-x-[200%] before:transition-transform before:duration-700",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-card hover:-translate-y-0.5 active:translate-y-0",
        outline:
          "border-2 border-accent-800 bg-transparent text-accent-800 hover:bg-accent-800/10 hover:border-accent-700 hover:-translate-y-0.5 active:translate-y-0",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0",
        ghost:
          "hover:bg-accent-800/10 hover:text-accent-800 hover:scale-105 active:scale-100",
        link: "text-accent-800 underline-offset-4 hover:underline hover:text-accent-700 active:text-accent-900",
        premium:
          "bg-gradient-to-r from-accent-800 via-accent-700 to-accent-800 bg-[length:200%_100%] text-white hover:shadow-glow hover:bg-[position:100%_0] hover:scale-[1.02] active:scale-[0.98] font-bold shadow-brand-hover transition-all duration-300 relative overflow-hidden",
        google:
          "bg-card border border-border text-card-foreground hover:bg-accent-800/10 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 active:translate-y-0",
      },
      size: {
        default: "h-10 px-6 py-2.5",
        sm: "h-9 rounded-md px-4 py-2 text-xs",
        lg: "h-12 rounded-lg px-8 py-3 text-base font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
